// Copyright (c) Bentley Systems
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  LabeledSelect,
  ProgressRadial,
  Text,
} from "@itwin/itwinui-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import {
  DEBUG_LOGGING_PROPAGATION_MINUTES,
  debugLoggingDurationOptions,
  DEFAULT_DEBUG_LOGGING_DAYS,
  formatDebugLoggingTime,
  isDebugLoggingActive,
  MAX_DEBUG_LOGGING_DAYS,
  ProjectDebugLogging,
} from "./Models/project-debug-logging";
import { useProjectDebugLoggingApi } from "./useProjectDebugLogging";

export type ProjectDebugLoggingFormProps = {
  packageUniqueName: string | undefined;
  iTwinId: string | undefined;
  /** Closes the form; `refreshList` is true when the Debug logging state changed. */
  closeFrollFunction?: (refreshList: boolean) => void;
};

const labelStyle = { flex: "0 0 150px" };
const rowStyle = { display: "flex", marginBottom: "8px", alignItems: "center" };

/**
 * Lets an architect switch Debug project logging on or off for one integration project on one
 * iTwin. Debug logging lasts at most {@link MAX_DEBUG_LOGGING_DAYS} days and then switches itself
 * off; switching it on again starts a new period rather than adding to the time left.
 */
const ProjectDebugLoggingForm = ({
  packageUniqueName,
  iTwinId,
  closeFrollFunction,
}: ProjectDebugLoggingFormProps) => {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectAccessToken);
  const { showToast } = useToastNotificationService();
  const { getDebugLogging, enableDebugLogging, disableDebugLogging } =
    useProjectDebugLoggingApi({ accessToken, dispatch });

  const [state, setState] = useState<ProjectDebugLogging>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [durationDays, setDurationDays] = useState(DEFAULT_DEBUG_LOGGING_DAYS);
  const durationOptions = useMemo(() => debugLoggingDurationOptions(), []);

  useEffect(() => {
    setState(undefined);
    if (!packageUniqueName || !iTwinId) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    setIsLoading(true);
    void getDebugLogging(packageUniqueName, iTwinId, abortController.signal).then(
      (result) => {
        if (abortController.signal.aborted) {
          return;
        }
        if (result.ok) {
          setState(result.state);
        } else if (result.message) {
          showToast({ status: "negative", message: result.message });
        }
        setIsLoading(false);
      },
    );

    return () => {
      abortController.abort();
    };
  }, [packageUniqueName, iTwinId, getDebugLogging, showToast]);

  const isOn = isDebugLoggingActive(state);
  const canChange = !!state && !!packageUniqueName && !!iTwinId && !isSaving;

  const closeFRoll = (refreshList: boolean) => {
    closeFrollFunction?.(refreshList);
  };

  const onTurnOn = async () => {
    if (!packageUniqueName || !iTwinId) {
      return;
    }
    setIsSaving(true);
    const result = await enableDebugLogging(packageUniqueName, iTwinId, durationDays);
    setIsSaving(false);
    if (result.ok) {
      showToast({
        status: "positive",
        message: `Debug logging is on for ${packageUniqueName} until ${formatDebugLoggingTime(result.state.expiresAtUtc)}. New runs pick it up within ${DEBUG_LOGGING_PROPAGATION_MINUTES} minutes.`,
      });
      closeFRoll(true);
    } else if (result.message) {
      showToast({ status: "negative", message: result.message });
    }
  };

  const onTurnOff = async () => {
    if (!packageUniqueName || !iTwinId) {
      return;
    }
    setIsSaving(true);
    const result = await disableDebugLogging(packageUniqueName, iTwinId);
    setIsSaving(false);
    if (result.ok) {
      showToast({
        status: "positive",
        message: `Debug logging is off for ${packageUniqueName}. New runs stop logging at Debug within ${DEBUG_LOGGING_PROPAGATION_MINUTES} minutes.`,
      });
      closeFRoll(true);
    } else if (result.message) {
      showToast({ status: "negative", message: result.message });
    }
  };

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <div
            className="becs-infopanelbody-footer-line"
            style={{ flex: 1, overflowY: "auto" }}
          >
            <div className="becs-all-margin">
              <Alert type="informational">
                Runs of this project on this iTwin keep only errors in the
                project log. With Debug logging on they keep every entry, until
                it switches itself off at the end of the chosen period (at most{" "}
                {MAX_DEBUG_LOGGING_DAYS} days). Turning it on or off reaches new
                runs within {DEBUG_LOGGING_PROPAGATION_MINUTES} minutes; runs
                already in progress keep the setting they started with.
              </Alert>

              {state ? (
                <div className="becs-t-margin">
                  <div style={rowStyle}>
                    <Text variant="body" style={labelStyle}>
                      Debug logging:
                    </Text>
                    {isOn ? (
                      <Badge backgroundColor="positive">On</Badge>
                    ) : (
                      <Text variant="body">
                        <b>Off</b>, only errors are logged
                      </Text>
                    )}
                  </div>
                  {isOn && (
                    <>
                      <div style={rowStyle}>
                        <Text variant="body" style={labelStyle}>
                          Switches off:
                        </Text>
                        <Text variant="body">
                          <b>{formatDebugLoggingTime(state.expiresAtUtc)}</b>
                        </Text>
                      </div>
                      <div style={rowStyle}>
                        <Text variant="body" style={labelStyle}>
                          Turned on:
                        </Text>
                        <Text variant="body">
                          <b>{formatDebugLoggingTime(state.enabledAtUtc)}</b>
                          {state.enabledByName ? ` by ${state.enabledByName}` : ""}
                        </Text>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="becs-t-margin">
                  <Alert type="negative">
                    The Debug logging state could not be loaded.
                  </Alert>
                </div>
              )}

              <div className="becs-t-margin">
                <LabeledSelect<number>
                  label={isOn ? "Restart for" : "Turn on for"}
                  options={durationOptions}
                  value={durationDays}
                  onChange={(value: number) => setDurationDays(value)}
                  disabled={!canChange}
                  message={
                    isOn
                      ? "Restarting starts a new period from now; it does not add to the time left."
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Button
                  styleType="cta"
                  disabled={!canChange}
                  onClick={() => void onTurnOn()}
                >
                  {isOn ? "Restart" : "Turn on"}
                </Button>
                {isOn && (
                  <Button
                    styleType="high-visibility"
                    className="becs-button-negative"
                    disabled={!canChange}
                    onClick={() => void onTurnOff()}
                  >
                    Turn off
                  </Button>
                )}
                <Button disabled={isSaving} onClick={() => closeFRoll(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDebugLoggingForm;
